import { getTranslation } from '@mahawi/eshop-common/src/translation';
import {
  type ILanguageType,
  type ITranslation,
} from '@mahawi/eshop-common/src/types';
import { type RootState } from '@reducers';
import { Button, Col, Divider, Form, Input, Row, Space, Spin } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  AddFocusEventListener,
  FORM_TEXT_HOTKEYS,
  FormTextHandler,
} from '../../fragments/form-events';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { EHotkey, useHotkey } from '../../hooks/use-hotkey';
import { nameLive, nameUpdate } from '../../reducers/property/reducer';
import { SPACING } from '../../theme/tokens';
import BubbleComponent from '../bubble';
import GoogleTranslate from '../google-translate';
import GoogleTranslateAutomat, {
  type ITextTranslate,
} from '../google-translate-automat';
import StatusIcon from '../status-icon';

interface IForm {
  name: string;
}

function PropertyName(): React.ReactElement {
  const dispatch = useAppDispatch();
  const languageType = useSelector((s: RootState) => s.Language.languageType);
  const languages = useSelector((s: RootState) => s.Language.languages);
  const property = useSelector((s: RootState) => s.Property.property);
  const propertyInProcess = useSelector((s: RootState) => s.Property.inProcess);

  const [form] = Form.useForm<IForm>();

  const [gtLinkName, setGtLinkName] = useState<string>('GT name EN → ---');
  const [invalidLanguageTranslations, setInvalidLanguageTranslations] =
    useState<Set<string>>(new Set());
  const [propertyNameEN, setPropertyNameEN] = useState<ITranslation>();

  const translateName: () => void = useCallback((): void => {
    AddFocusEventListener(form, (text: string): void => {
      form.setFieldsValue({
        name: text,
      });
      if (property?.code && languageType?.code) {
        dispatch(nameLive(property.code, text, languageType.code));
      }
    });
  }, [languageType?.code, dispatch, form, property]);

  useHotkey(FORM_TEXT_HOTKEYS, (hotkey: EHotkey): void => {
    FormTextHandler(form, hotkey, (text: string): void => {
      if (property?.code && languageType?.code) {
        dispatch(nameLive(property.code, text, languageType.code));
      }
    });
  });

  const onFinish: (values: IForm) => void = useCallback(
    ({ name }: IForm): void => {
      if (property && languageType?.code) {
        dispatch(nameUpdate(property.code, name, languageType.code));
      }
    },
    [dispatch, property, languageType?.code],
  );

  useEffect((): void => {
    const propertyNameENUE: ITranslation | undefined = property?.names.find(
      ({ code }: ITranslation): boolean => code === 'EN',
    );
    setPropertyNameEN(propertyNameENUE);

    const propertyNameUE: string | null = getTranslation(
      property?.names,
      languageType,
    );

    const propertyNameTranslatedUE: string = propertyNameUE
      ? propertyNameUE
      : '';

    form.setFieldsValue({
      name: propertyNameTranslatedUE,
    });

    setGtLinkName(`GT name EN → ${languageType?.code}`);
  }, [languageType?.code, property, form]);

  useEffect((): void => {
    const invalidLanguageTranslationsUE = new Set<string>();

    languages?.forEach((language: ILanguageType): void => {
      const cn: ITranslation | undefined = property?.names.find(
        (p: ITranslation): boolean => p.code === language.code,
      );

      if (!cn || cn.value.length <= 2) {
        invalidLanguageTranslationsUE.add(language.code);
      }
    });

    setInvalidLanguageTranslations(invalidLanguageTranslationsUE);
  }, [languages, languageType?.code, property]);

  const propertyNameTranslated: string =
    propertyNameEN?.value || `** ${property?.code} **`;

  const texts: ITextTranslate[] = [
    {
      text: propertyNameTranslated,
      callbacks: [
        (text: string, lc: string): void => {
          form.setFieldValue('name', text);

          if (property?.code) {
            dispatch(nameLive(property.code, text, lc));
          }
        },
      ],
    },
  ];

  return (
    <BubbleComponent span={8}>
      <Space size={SPACING.lg} align="start" orientation="horizontal">
        <strong>Name</strong>
        <StatusIcon ok={invalidLanguageTranslations.size === 0} />
        {invalidLanguageTranslations.size > 0 && (
          <small>
            {[...invalidLanguageTranslations]
              .map((code: string): string => code)
              .join(', ')}
          </small>
        )}
      </Space>

      <Form form={form} onFinish={(values: IForm): void => onFinish(values)}>
        <Row>
          <Col xs={24} md={8}>
            <Space size={SPACING.lg} align="start">
              <Button
                type="primary"
                htmlType="submit"
                disabled={propertyInProcess}
              >
                Submit
              </Button>
              <Spin size="small" spinning={propertyInProcess} />
            </Space>
          </Col>

          <Col xs={24} md={8}>
            <GoogleTranslateAutomat
              texts={texts}
              submit={form.submit}
              isSaving={propertyInProcess}
            />
          </Col>

          <Col xs={24} md={8}>
            <GoogleTranslate
              fromLanguageCode="EN"
              toLanguageCode={languageType?.code}
              name={gtLinkName}
              text={propertyNameTranslated}
              onClick={(): void => translateName()}
            />
          </Col>
        </Row>

        <Divider />

        <Row>
          <Col span={24}>
            <Form.Item
              name="name"
              rules={[
                {
                  required: true,
                },
                {
                  type: 'string',
                  min: 2,
                },
              ]}
              label="Name"
            >
              <Input placeholder="Name" disabled={propertyInProcess} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </BubbleComponent>
  );
}

export default PropertyName;
